(() => {
  'use strict';

  const DEFAULT_CONFIG = {
    appSheetId: 'd6652927-4eea-4e73-b226-e069b83fc7bf',
    webAppUrl: 'https://script.google.com/macros/s/AKfycbx_3nuGKqixtb3YOzAH04RO9umvzX4vQv7wChEFXXCE4BKxkANbOokt4lHFpGiuooWV/exec'
  };

  const CONFIG = {
    ...DEFAULT_CONFIG,
    ...(window.PRODUCT_INTAKE_CONFIG || {})
  };

  const MY_APPSHEET_ID = CONFIG.appSheetId;
  const WEB_APP_URL = CONFIG.webAppUrl;
  const CLIENT_BUILD_ID = '2026-09-12-grounded-catalog-enrichment-v1';
  const WORKER_SESSION_STORAGE_KEY = 'gf-intake-worker-session-v1';
  const CLIENT_SESSION_ID = restoreClientSessionId_() || createClientRequestId();
  const EMPTY_GIF = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
  const IMAGE_PIPELINE_CONCURRENCY = 4;
  const STAFF_ACTIONS = new Set(['uploadImage', 'analyzeImages', 'submitFinal']);
  const API_ERROR_MESSAGES = Object.freeze({
    AI_ANALYSIS_UNAVAILABLE:
      'AI analysis is temporarily unavailable. Your photos are saved—retry analysis later.'
  });
  const REFERENCE_DATA_CACHE_KEY = 'gf-product-intake-reference-data-v5';
  const REFERENCE_DATA_CACHE_TTL_MS = 5 * 60 * 1000;
  const VALID_DRAFT_STATUSES = new Set(['uploading', 'analyzing', 'ready', 'error', 'submitted']);

  let html5QrCode = null;
  let activeInputId = null;
  let selectedFiles = [];
  let drafts = {};
  let currentReviewDraftId = null;
  let shippingAttributesByProductType = {};
  let shippingClassChoices = [];
  let shippingOverrideRequiredProductTypes = new Set();
  let referenceDataStatus = 'idle';
  let productTypeReferenceReady = false;
  let workerSession = null;
  let workerSessionPromise = null;
  let workerSessionResolve = null;
  let workerAuthPending = false;
  let draftCreationPending = false;

  const imagePreviewUrls = new WeakMap();
  const stagedFileReads = new WeakMap();
  const runImagePipelineTask = createTaskLimiter(IMAGE_PIPELINE_CONCURRENCY);
  const dom = {};

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    cacheDom();
    bindEvents();
    initCursorGuitarTrail();
    renderDraftsQueue();
    checkIntakeReady();
    initializeIntake_();
  }

  async function initializeIntake_() {
    await ensureWorkerSession_();
    setWorkerAuthGate_(false);
    loadReferenceData();
    checkIntakeReady();
  }

  function cacheDom() {
    dom.workerAuthGate = document.getElementById('worker-auth-gate');
    dom.workerAuthForm = document.getElementById('worker-auth-form');
    dom.workerPasswordInput = document.getElementById('worker-password');
    dom.workerAuthSubmit = document.getElementById('worker-auth-submit');
    dom.workerAuthStatus = document.getElementById('worker-auth-status');
    dom.intakeApp = document.querySelector('.product-intake-app');
    dom.scannerModal = document.getElementById('scanner-modal');
    dom.scannerCancelBtn = document.getElementById('scanner-cancel-btn');
    dom.cameraInput = document.getElementById('camera-input');
    dom.galleryInput = document.getElementById('gallery-input');
    dom.itemInfoInput = document.getElementById('item-info-input');
    dom.productLocationInput = document.getElementById('intake-productLocation');
    dom.caseLocationInput = document.getElementById('intake-caseLocation');
    dom.locationOptions = document.getElementById('location-options');
    dom.categoryOptions = document.getElementById('category-options');
    dom.typeSelect = document.getElementById('edit-type');
    dom.shippingAttributesSection = document.getElementById('shipping-attributes-section');
    dom.shippingAttributeFields = document.getElementById('shipping-attribute-fields');
    dom.shippingClassOverride = document.getElementById('shipping-class-override');
    dom.shippingOverrideHelp = document.getElementById('shipping-override-help');
    dom.identificationReview = document.getElementById('identification-review');
    dom.identificationStatus = document.getElementById('identification-status');
    dom.identificationSummary = document.getElementById('identification-summary');
    dom.identificationEvidence = document.getElementById('identification-evidence');
    dom.identificationUncertainties = document.getElementById('identification-uncertainties');
    dom.catalogResearchReview = document.getElementById('catalog-research-review');
    dom.catalogResearchNotes = document.getElementById('catalog-research-notes');
    dom.catalogResearchSources = document.getElementById('catalog-research-sources');
    dom.analysisModelName = document.getElementById('analysis-model-name');
    dom.retryModelSelect = document.getElementById('retry-model-select');
    dom.retryModelBtn = document.getElementById('retry-model-btn');
    dom.retryModelStatus = document.getElementById('retry-model-status');
    dom.analyzeBtn = document.getElementById('analyze-btn');
    dom.previewGrid = document.getElementById('preview-grid');
    dom.draftsList = document.getElementById('drafts-list');
    dom.reviewModal = document.getElementById('review-modal');
    dom.reviewCloseBtn = document.getElementById('review-close-btn');
    dom.submitShopifyBtn = document.getElementById('submit-shopify-btn');
  }

  function bindEvents() {
    dom.workerAuthForm?.addEventListener('submit', handleWorkerSessionSubmit_);
    dom.cameraInput?.addEventListener('change', handleFileInputChange);
    dom.galleryInput?.addEventListener('change', handleFileInputChange);
    dom.itemInfoInput?.addEventListener('input', checkIntakeReady);
    dom.analyzeBtn?.addEventListener('click', createDraftJob);
    dom.scannerCancelBtn?.addEventListener('click', () => stopScanner());
    dom.reviewCloseBtn?.addEventListener('click', closeReviewModal);
    dom.retryModelBtn?.addEventListener('click', retryAnalysisWithSelectedModel);
    dom.submitShopifyBtn?.addEventListener('click', submitDraftToShopify);
    dom.typeSelect?.addEventListener('change', renderShippingAttributeFields);
    dom.shippingAttributeFields?.addEventListener(
      'change',
      updateShippingOverrideRequirement
    );

    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleDocumentKeydown);
  }

  function initCursorGuitarTrail() {
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    let enabled = !reducedMotionQuery.matches;
    let lastTime = 0;
    let pendingFrame = false;
    const lastPoint = { x: 0, y: 0 };
    const guitars = new Set();
    const minInterval = 30;
    const maxGuitars = 70;

    const clearGuitars = () => {
      for (const guitar of guitars) {
        guitar.remove();
      }

      guitars.clear();
    };

    const handleMotionPreferenceChange = event => {
      enabled = !event.matches;
      if (!enabled) clearGuitars();
    };

    if (typeof reducedMotionQuery.addEventListener === 'function') {
      reducedMotionQuery.addEventListener('change', handleMotionPreferenceChange);
    } else if (typeof reducedMotionQuery.addListener === 'function') {
      reducedMotionQuery.addListener(handleMotionPreferenceChange);
    }

    document.addEventListener('pointermove', event => {
      if (!enabled || event.pointerType === 'touch') return;

      lastPoint.x = event.clientX;
      lastPoint.y = event.clientY;

      const now = performance.now();
      if (pendingFrame || now - lastTime < minInterval) return;

      pendingFrame = true;

      requestAnimationFrame(() => {
        pendingFrame = false;
        lastTime = performance.now();
        createCursorGuitar(lastPoint.x, lastPoint.y, guitars, maxGuitars);
      });
    }, { passive: true });
  }

  function createCursorGuitar(x, y, guitars, maxGuitars) {
    if (!document.body) return;

    if (guitars.size >= maxGuitars) {
      const oldestGuitar = guitars.values().next().value;
      oldestGuitar?.remove();
      guitars.delete(oldestGuitar);
    }

    const guitar = document.createElement('span');
    const glyphs = ['🎸'];
    const angle = Math.random() * Math.PI * 2;
    const distance = 16 + Math.random() * 38;
    const size = 18 + Math.random() * 16;
    const hue = Math.floor(Math.random() * 360);
    const rotation = Math.floor(180 + Math.random() * 520);

    guitar.className = 'cursor-guitar-trail';
    guitar.setAttribute('aria-hidden', 'true');
    guitar.textContent = glyphs[Math.floor(Math.random() * glyphs.length)];
    guitar.style.left = `${x}px`;
    guitar.style.top = `${y}px`;
    guitar.style.setProperty('--guitar-size', `${size}px`);
    guitar.style.setProperty('--guitar-hue', String(hue));
    guitar.style.setProperty('--guitar-x', `${Math.cos(angle) * distance}px`);
    guitar.style.setProperty('--guitar-y', `${Math.sin(angle) * distance}px`);
    guitar.style.setProperty('--guitar-rotate', `${rotation}deg`);

    guitars.add(guitar);
    document.body.appendChild(guitar);

    guitar.addEventListener('animationend', () => {
      guitar.remove();
      guitars.delete(guitar);
    }, { once: true });
  }

  function handleDocumentClick(event) {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const scannerTrigger = target.closest('[data-scanner-target]');
    if (!scannerTrigger) return;

    const inputId = scannerTrigger.getAttribute('data-scanner-target');
    if (inputId) startScanner(inputId);
  }

  function handleDocumentKeydown(event) {
    if (event.key !== 'Escape') return;

    if (dom.scannerModal && !dom.scannerModal.hidden) {
      stopScanner();
      return;
    }

    if (dom.reviewModal && !dom.reviewModal.hidden) {
      closeReviewModal();
    }
  }

  // ── Scanner Logic ──────────────────────────────────────

  async function startScanner(inputId) {
    try {
      activeInputId = inputId;

      if (!dom.scannerModal) {
        alert('Scanner modal is missing from the page.');
        return;
      }

      dom.scannerModal.hidden = false;

      if (typeof window.Html5Qrcode === 'undefined') {
        alert('QR Library failed to load. Check your Shopify script tag / theme asset.');
        await stopScanner();
        return;
      }

      if (html5QrCode) {
        await stopScanner(false);
        activeInputId = inputId;
        dom.scannerModal.hidden = false;
      }

      html5QrCode = new window.Html5Qrcode('reader');

      await html5QrCode.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        decodedText => {
          const input = document.getElementById(activeInputId);
          if (input) input.value = decodedText;
          stopScanner();
        },
        () => {
          // Ignore background frame decode errors.
        }
      );
    } catch (err) {
      alert(`Scanner crashed: ${err.message || err}`);
      await stopScanner();
    }
  }

  async function stopScanner(hideModal = true) {
    if (html5QrCode) {
      try {
        await html5QrCode.stop();
      } catch (err) {
        console.warn('QR scanner stop warning:', err);
      }

      try {
        html5QrCode.clear();
      } catch (err) {
        console.warn('QR scanner clear warning:', err);
      }
    }

    html5QrCode = null;
    activeInputId = null;

    if (hideModal && dom.scannerModal) {
      dom.scannerModal.hidden = true;
    }
  }

  // ── API Logic ──────────────────────────────────────────

  function createClientRequestId() {
    if (globalThis.crypto?.randomUUID) {
      return globalThis.crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  async function apiFetch(action, payloadData = {}) {
    if (!WEB_APP_URL) {
      throw new Error('Missing Web App URL configuration.');
    }

    const requestId = String(payloadData.requestId || createClientRequestId());
    const clientSentAt = new Date().toISOString();
    const payload = {
      action,
      ...payloadData,
      source: 'shopify_product_intake',
      clientSessionId: CLIENT_SESSION_ID,
      clientSentAt,
      clientBuild: CLIENT_BUILD_ID,
      requestId
    };
    const requiresStaffSession = STAFF_ACTIONS.has(action);

    for (let authAttempt = 0; authAttempt < 2; authAttempt++) {
      if (requiresStaffSession) {
        payload.workerSessionToken = await ensureWorkerSession_();
      }
      const json = await sendApiPayload_(payload);
      if (json.status !== 'error') return json.data;

      if (
        requiresStaffSession &&
        authAttempt === 0 &&
        json.code === 'INTAKE_AUTH_REQUIRED'
      ) {
        clearWorkerSession_();
        continue;
      }
      throw apiResponseError_(json);
    }

    throw new Error('Staff sign-in failed.');
  }

  async function sendApiPayload_(payload) {
    const requestUrl = new URL(WEB_APP_URL, window.location.href);
    const queryFields = {
      source: payload.source,
      action: payload.action,
      requestId: payload.requestId,
      clientSessionId: CLIENT_SESSION_ID,
      clientSentAt: payload.clientSentAt,
      clientBuild: CLIENT_BUILD_ID,
      intendedMethod: 'POST',
      draftId: payload.draftId || '',
      requestPurpose: payload.requestPurpose || '',
      retryOfRequestId: payload.retryOfRequestId || ''
    };
    Object.entries(queryFields).forEach(([key, value]) => {
      if (value) requestUrl.searchParams.set(key, String(value));
    });

    const response = await fetch(requestUrl.toString(), {
      method: 'POST',
      redirect: 'follow',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      throw new Error(`API request failed with HTTP ${response.status}.`);
    }

    let json;
    try {
      json = await response.json();
    } catch (err) {
      throw new Error('API returned an invalid JSON response.');
    }

    return json;
  }

  async function ensureWorkerSession_() {
    const current = readWorkerSession_();
    if (current) return current.token;
    if (!workerSessionPromise) {
      workerSessionPromise = requestWorkerSession_()
        .finally(() => { workerSessionPromise = null; });
    }
    const session = await workerSessionPromise;
    return session.token;
  }

  function requestWorkerSession_() {
    setWorkerAuthStatus_('');
    setWorkerAuthGate_(true);
    dom.workerPasswordInput?.focus();
    return new Promise(resolve => {
      workerSessionResolve = resolve;
    });
  }

  async function handleWorkerSessionSubmit_(event) {
    event.preventDefault();
    if (workerAuthPending) return;

    let password = String(dom.workerPasswordInput?.value || '');
    if (!password) {
      setWorkerAuthStatus_('Enter the staff password.');
      dom.workerPasswordInput?.focus();
      return;
    }

    const payload = {
      action: 'createIntakeSession',
      workerPassword: password,
      source: 'shopify_product_intake',
      clientSessionId: CLIENT_SESSION_ID,
      clientSentAt: new Date().toISOString(),
      clientBuild: CLIENT_BUILD_ID,
      requestId: createClientRequestId(),
      requestPurpose: 'staff_sign_in'
    };
    workerAuthPending = true;
    if (dom.workerAuthSubmit) dom.workerAuthSubmit.disabled = true;
    setWorkerAuthStatus_('Checking password…');

    try {
      const json = await sendApiPayload_(payload);
      if (json.status === 'error') {
        if (json.code === 'INTAKE_AUTH_REQUIRED') {
          setWorkerAuthStatus_('Incorrect password. Try again.');
          return;
        }
        throw apiResponseError_(json);
      }

      const session = {
        token: String(json.data?.token || ''),
        expiresAt: Number(json.data?.expiresAt) || 0,
        clientSessionId: CLIENT_SESSION_ID
      };
      if (!session.token || session.expiresAt <= Date.now()) {
        throw new Error('The server returned an invalid staff session.');
      }

      saveWorkerSession_(session);
      setWorkerAuthGate_(false);
      const resolveSession = workerSessionResolve;
      workerSessionResolve = null;
      resolveSession?.(session);
    } catch (err) {
      setWorkerAuthStatus_(err.message || String(err));
    } finally {
      payload.workerPassword = '';
      password = '';
      if (dom.workerPasswordInput) dom.workerPasswordInput.value = '';
      workerAuthPending = false;
      if (dom.workerAuthSubmit) dom.workerAuthSubmit.disabled = false;
      if (!dom.workerAuthGate?.hidden) dom.workerPasswordInput?.focus();
    }
  }

  function setWorkerAuthGate_(visible) {
    if (dom.workerAuthGate) dom.workerAuthGate.hidden = !visible;
    if (dom.intakeApp) dom.intakeApp.inert = visible;
    document.body.classList.toggle('worker-auth-open', visible);
  }

  function setWorkerAuthStatus_(message) {
    if (dom.workerAuthStatus) dom.workerAuthStatus.textContent = message;
  }

  function readWorkerSession_() {
    if (workerSession?.token && workerSession.expiresAt > Date.now() + 30000) {
      return workerSession;
    }
    try {
      const stored = JSON.parse(
        localStorage.getItem(WORKER_SESSION_STORAGE_KEY) || 'null'
      );
      if (
        stored?.token &&
        stored.clientSessionId === CLIENT_SESSION_ID &&
        Number(stored.expiresAt) > Date.now() + 30000
      ) {
        workerSession = stored;
        return stored;
      }
    } catch (err) {
      console.warn('Could not read the staff session from localStorage.', err);
    }
    clearWorkerSession_();
    return null;
  }

  function saveWorkerSession_(session) {
    workerSession = session;
    try {
      localStorage.setItem(WORKER_SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch (err) {
      console.warn('Could not persist the staff session in localStorage.', err);
    }
  }

  function clearWorkerSession_() {
    workerSession = null;
    try {
      localStorage.removeItem(WORKER_SESSION_STORAGE_KEY);
      sessionStorage.removeItem(WORKER_SESSION_STORAGE_KEY);
    } catch (err) {
      console.warn('Could not clear the staff session from browser storage.', err);
    }
  }

  function restoreClientSessionId_() {
    try {
      const stored = JSON.parse(
        localStorage.getItem(WORKER_SESSION_STORAGE_KEY) || 'null'
      );
      if (
        stored?.token &&
        stored.clientSessionId &&
        Number(stored.expiresAt) > Date.now() + 30000
      ) {
        return String(stored.clientSessionId);
      }
    } catch (err) {
      // Storage can be unavailable in private or embedded browser contexts.
    }
    return '';
  }

  function apiResponseError_(json) {
    const code = String(json.code || '');
    const error = new Error(
      API_ERROR_MESSAGES[code] || json.message || 'API returned an error.'
    );
    error.code = code;
    return error;
  }

  async function loadReferenceData() {
    if (referenceDataStatus === 'loading') return;
    const cached = readReferenceDataCache();
    if (cached) {
      populateReferenceData(cached);
      referenceDataStatus = 'ready';
      return;
    }

    referenceDataStatus = 'loading';
    try {
      const referenceData = await apiFetch('getIntakeReferenceData');
      if (!hasCompleteReferenceData(referenceData)) {
        throw new Error('Combined intake reference data was incomplete.');
      }
      populateReferenceData(referenceData);
      writeReferenceDataCache(referenceData);
      referenceDataStatus = 'ready';
    } catch (combinedError) {
      console.warn('Combined intake reference-data request failed; using compatibility requests:', combinedError);
      const [locationsResult, categoriesResult, productTypesResult] = await Promise.allSettled([
        apiFetch('getLocations'),
        apiFetch('getCategories'),
        apiFetch('getProductTypeReferenceData')
      ]);
      const productTypeReference = productTypesResult.status === 'fulfilled'
        ? productTypesResult.value
        : {};
      const referenceData = {
        locations: locationsResult.status === 'fulfilled'
          ? locationsResult.value
          : [],
        categories: categoriesResult.status === 'fulfilled'
          ? categoriesResult.value
          : [],
        productTypes: productTypeReference.productTypes || [],
        shippingAttributes: productTypeReference.shippingAttributes || {},
        shippingClassChoices: productTypeReference.shippingClassChoices || [],
        shippingOverrideRequiredProductTypes:
          productTypeReference.shippingOverrideRequiredProductTypes || []
      };
      populateReferenceData(referenceData);
      if (hasCompleteReferenceData(referenceData)) {
        writeReferenceDataCache(referenceData);
        referenceDataStatus = 'ready';
      } else {
        referenceDataStatus = 'error';
        markProductTypeReferenceUnavailable();
      }
    }
  }

  function populateReferenceData(referenceData = {}) {
    populateDatalist(dom.locationOptions, referenceData.locations);
    populateDatalist(dom.categoryOptions, referenceData.categories);
    const productTypes = normalizeReferenceValues(referenceData.productTypes);
    if (productTypes.length > 0) {
      populateSelect(dom.typeSelect, productTypes);
      productTypeReferenceReady = hasCompleteProductTypeReferenceData(
        referenceData
      );
      if (dom.typeSelect) {
        dom.typeSelect.disabled = !productTypeReferenceReady;
      }
    }
    shippingAttributesByProductType =
      referenceData.shippingAttributes &&
      typeof referenceData.shippingAttributes === 'object'
        ? referenceData.shippingAttributes
        : {};
    shippingClassChoices = Array.isArray(referenceData.shippingClassChoices)
      ? referenceData.shippingClassChoices
      : [];
    shippingOverrideRequiredProductTypes = new Set(
      Array.isArray(referenceData.shippingOverrideRequiredProductTypes)
        ? referenceData.shippingOverrideRequiredProductTypes
        : []
    );
    populateShippingOverrideSelect();
    renderShippingAttributeFields();
  }

  function hasCompleteReferenceData(referenceData) {
    return normalizeReferenceValues(referenceData?.locations).length > 0 &&
      normalizeReferenceValues(referenceData?.categories).length > 0 &&
      hasCompleteProductTypeReferenceData(referenceData);
  }

  function hasCompleteProductTypeReferenceData(referenceData) {
    return Array.isArray(referenceData?.productTypes) &&
      normalizeReferenceValues(referenceData.productTypes).length > 0 &&
      referenceData?.shippingAttributes &&
      typeof referenceData.shippingAttributes === 'object' &&
      Array.isArray(referenceData?.shippingClassChoices) &&
      referenceData.shippingClassChoices.length > 0;
  }

  function normalizeReferenceValues(values) {
    if (!Array.isArray(values)) return [];
    return [...new Set(values.map(value => String(value || '').trim()).filter(Boolean))];
  }

  function readReferenceDataCache() {
    try {
      const cached = JSON.parse(
        window.localStorage.getItem(REFERENCE_DATA_CACHE_KEY) || 'null'
      );
      if (
        !cached ||
        cached.expiresAt <= Date.now() ||
        !hasCompleteReferenceData(cached)
      ) {
        window.localStorage.removeItem(REFERENCE_DATA_CACHE_KEY);
        return null;
      }
      return cached;
    } catch (err) {
      return null;
    }
  }

  function writeReferenceDataCache(referenceData = {}) {
    try {
      window.localStorage.setItem(
        REFERENCE_DATA_CACHE_KEY,
        JSON.stringify({
          locations: Array.isArray(referenceData.locations)
            ? referenceData.locations
            : [],
          categories: Array.isArray(referenceData.categories)
            ? referenceData.categories
            : [],
          productTypes: Array.isArray(referenceData.productTypes)
            ? referenceData.productTypes
            : [],
          shippingAttributes:
            referenceData.shippingAttributes &&
            typeof referenceData.shippingAttributes === 'object'
              ? referenceData.shippingAttributes
              : {},
          shippingClassChoices: Array.isArray(referenceData.shippingClassChoices)
            ? referenceData.shippingClassChoices
            : [],
          shippingOverrideRequiredProductTypes:
            Array.isArray(referenceData.shippingOverrideRequiredProductTypes)
              ? referenceData.shippingOverrideRequiredProductTypes
              : [],
          expiresAt: Date.now() + REFERENCE_DATA_CACHE_TTL_MS
        })
      );
    } catch (err) {
      // Some embedded browsers disable storage; reference data is still usable.
    }
  }

  function populateDatalist(listElement, values) {
    if (!listElement || !Array.isArray(values)) return;

    const fragment = document.createDocumentFragment();

    for (const value of values) {
      const option = document.createElement('option');
      option.value = value == null ? '' : String(value);
      fragment.appendChild(option);
    }

    listElement.replaceChildren(fragment);
  }

  function populateSelect(selectElement, values) {
    if (!selectElement || !Array.isArray(values)) return;

    const currentValue = selectElement.value;
    const fragment = document.createDocumentFragment();
    const emptyOption = document.createElement('option');
    emptyOption.value = '';
    emptyOption.textContent = '-- Select Type --';
    fragment.appendChild(emptyOption);

    for (const value of values) {
      const option = document.createElement('option');
      option.value = value == null ? '' : String(value);
      option.textContent = option.value;
      fragment.appendChild(option);
    }

    if (currentValue && !values.includes(currentValue)) {
      const preservedOption = document.createElement('option');
      preservedOption.value = currentValue;
      preservedOption.textContent = `${currentValue} (AI selected)`;
      fragment.appendChild(preservedOption);
    }

    selectElement.replaceChildren(fragment);
    if (currentValue) selectElement.value = currentValue;
  }

  function setProductTypeReviewValue(value) {
    const normalized = String(value || '').trim();
    if (!dom.typeSelect) return;
    if (!normalized) {
      dom.typeSelect.value = '';
      return;
    }

    const hasOption = Array.from(dom.typeSelect.options)
      .some(option => option.value === normalized);
    if (!hasOption) {
      const option = document.createElement('option');
      option.value = normalized;
      option.textContent = `${normalized} (AI selected)`;
      dom.typeSelect.appendChild(option);
    }
    dom.typeSelect.value = normalized;
  }

  function markProductTypeReferenceUnavailable() {
    if (!dom.typeSelect || productTypeReferenceReady) return;
    const currentValue = dom.typeSelect.value;
    if (currentValue) return;
    const unavailableOption = document.createElement('option');
    unavailableOption.value = '';
    unavailableOption.textContent = '-- Product Types unavailable; reload --';
    dom.typeSelect.replaceChildren(unavailableOption);
    dom.typeSelect.disabled = true;
  }

  function populateShippingOverrideSelect() {
    if (!dom.shippingClassOverride) return;
    const currentValue = dom.shippingClassOverride.value;
    const fragment = document.createDocumentFragment();
    const emptyOption = document.createElement('option');
    emptyOption.value = '';
    emptyOption.textContent = '-- No Manual Override --';
    fragment.appendChild(emptyOption);
    shippingClassChoices.forEach(value => {
      const option = document.createElement('option');
      option.value = String(value);
      option.textContent = String(value);
      fragment.appendChild(option);
    });
    dom.shippingClassOverride.replaceChildren(fragment);
    if (shippingClassChoices.includes(currentValue)) {
      dom.shippingClassOverride.value = currentValue;
    }
  }

  function renderShippingAttributeFields() {
    if (!dom.shippingAttributeFields || !dom.shippingAttributesSection) return;
    const productType = dom.typeSelect?.value || '';
    const definitions = Array.isArray(
      shippingAttributesByProductType[productType]
    ) ? shippingAttributesByProductType[productType] : [];
    const fragment = document.createDocumentFragment();

    definitions.forEach(definition => {
      const field = document.createElement('div');
      field.className = 'field';
      const label = document.createElement('label');
      const select = document.createElement('select');
      const id = `shipping-attribute-${definition.key}`;
      label.className = 'input-label';
      label.htmlFor = id;
      label.textContent = `${definition.label}${definition.required ? ' *' : ''}`;
      select.id = id;
      select.className = 'input-field';
      select.dataset.shippingAttributeKey = definition.key;
      select.dataset.shippingAttributeLabel = definition.label;
      select.dataset.required = definition.required ? 'true' : 'false';
      const emptyOption = document.createElement('option');
      emptyOption.value = '';
      emptyOption.textContent = `-- Select ${definition.label} --`;
      select.appendChild(emptyOption);
      (definition.options || []).forEach(optionDefinition => {
        const option = document.createElement('option');
        option.value = String(optionDefinition.value || '');
        option.textContent = option.value;
        option.dataset.requiresOverride =
          optionDefinition.requiresOverride ? 'true' : 'false';
        select.appendChild(option);
      });
      field.append(label, select);
      fragment.appendChild(field);
    });

    dom.shippingAttributeFields.replaceChildren(fragment);
    dom.shippingAttributesSection.hidden = !productType;
    if (dom.shippingClassOverride) dom.shippingClassOverride.value = '';
    updateShippingOverrideRequirement();
  }

  function updateShippingOverrideRequirement() {
    const productType = dom.typeSelect?.value || '';
    const optionRequiresOverride = Array.from(
      dom.shippingAttributeFields?.querySelectorAll('select') || []
    ).some(select =>
      select.selectedOptions?.[0]?.dataset.requiresOverride === 'true'
    );
    const required =
      shippingOverrideRequiredProductTypes.has(productType) ||
      optionRequiresOverride;
    if (dom.shippingClassOverride) {
      dom.shippingClassOverride.required = required;
    }
    if (dom.shippingOverrideHelp) {
      dom.shippingOverrideHelp.textContent = required
        ? 'Required for this Product Type or selected shipping attribute.'
        : 'Optional. Use only when the normal Product Type and shipping selections do not fit.';
    }
  }

  function collectShippingReviewData() {
    const attributes = {};
    const missingLabels = [];
    let selectedOptionRequiresOverride = false;
    Array.from(
      dom.shippingAttributeFields?.querySelectorAll('select') || []
    ).forEach(select => {
      const value = select.value.trim();
      if (!value && select.dataset.required === 'true') {
        missingLabels.push(select.dataset.shippingAttributeLabel);
      }
      if (value) attributes[select.dataset.shippingAttributeKey] = value;
      if (
        value &&
        select.selectedOptions?.[0]?.dataset.requiresOverride === 'true'
      ) {
        selectedOptionRequiresOverride = true;
      }
    });
    const override = dom.shippingClassOverride?.value || '';
    const productType = dom.typeSelect?.value || '';
    const overrideRequired =
      shippingOverrideRequiredProductTypes.has(productType) ||
      selectedOptionRequiresOverride;
    return { attributes, missingLabels, override, overrideRequired };
  }

  // ── Intake Form State ──────────────────────────────────

  function handleFileInputChange(event) {
    const input = event.currentTarget;
    const files = Array.from(input.files || []);

    addFiles(files);

    // Allows users to choose the same file again after removing it.
    input.value = '';
  }

  function addFiles(files) {
    if (!files.length) return;

    files.forEach(stageFileRead);
    selectedFiles = selectedFiles.concat(files);
    renderPreviews();
    checkIntakeReady();
  }

  function renderPreviews() {
    if (!dom.previewGrid) return;

    dom.previewGrid.replaceChildren();

    selectedFiles.forEach((file, index) => {
      buildThumbnail(getPreviewUrl(file), index);
    });
  }

  function getPreviewUrl(file) {
    if (!file) return EMPTY_GIF;
    const existingUrl = imagePreviewUrls.get(file);
    if (existingUrl) return existingUrl;
    if (!window.URL?.createObjectURL) return EMPTY_GIF;
    const previewUrl = window.URL.createObjectURL(file);
    imagePreviewUrls.set(file, previewUrl);
    return previewUrl;
  }

  function revokeFilePreview(file) {
    const previewUrl = file ? imagePreviewUrls.get(file) : '';
    if (!previewUrl) return;
    window.URL?.revokeObjectURL?.(previewUrl);
    imagePreviewUrls.delete(file);
  }

  function revokePreviewUrl(previewUrl) {
    if (!previewUrl || !previewUrl.startsWith('blob:')) return;
    window.URL?.revokeObjectURL?.(previewUrl);
  }

  function buildThumbnail(dataUrl, index) {
    if (!dom.previewGrid) return;

    const item = document.createElement('div');
    item.className = 'preview-item';

    const image = document.createElement('img');
    image.src = dataUrl || EMPTY_GIF;
    image.alt = index === 0 ? 'Selected product cover photo preview' : `Selected product photo preview ${index + 1}`;

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'remove';
    removeButton.setAttribute('aria-label', `Remove photo ${index + 1}`);
    removeButton.textContent = '×';
    removeButton.addEventListener('click', event => {
      event.stopPropagation();
      removeFile(index);
    });

    const badge = document.createElement('div');
    badge.className = 'preview-badge';
    badge.textContent = index === 0 ? 'COVER' : String(index + 1);

    item.append(image, removeButton, badge);
    dom.previewGrid.appendChild(item);
  }

  function removeFile(index) {
    const removedFile = selectedFiles[index];

    selectedFiles.splice(index, 1);

    if (removedFile) {
      revokeFilePreview(removedFile);
      stagedFileReads.delete(removedFile);
    }

    renderPreviews();
    checkIntakeReady();
  }

  function checkIntakeReady() {
    if (!dom.analyzeBtn) return;

    const hasFiles = selectedFiles.length > 0;
    const hasText = (dom.itemInfoInput?.value || '').trim() !== '';
    const authRequired = Boolean(dom.workerAuthGate && !dom.workerAuthGate.hidden);

    dom.analyzeBtn.disabled = authRequired || !(hasFiles || hasText);
  }

  // ── Background Job Queue Logic ─────────────────────────

  async function createDraftJob() {
    if (dom.analyzeBtn?.disabled || draftCreationPending) return;
    draftCreationPending = true;
    try {
      await ensureWorkerSession_();
    } catch (err) {
      alert(err.message || String(err));
      return;
    } finally {
      draftCreationPending = false;
    }

    const draftId = `draft_${Date.now()}`;
    const infoText = (dom.itemInfoInput?.value || '').trim();
    const draftFiles = [...selectedFiles];
    const firstFile = draftFiles[0] || null;
    const thumbUrl = firstFile ? getPreviewUrl(firstFile) : null;

    drafts[draftId] = {
      id: draftId,
      files: draftFiles,
      fileIds: [],
      thumbUrl,
      info: infoText,
      productLoc: dom.productLocationInput?.value || '',
      caseLoc: dom.caseLocationInput?.value || '',
      status: 'uploading',
      progressText: 'Queued...',
      aiData: null,
      appSheetSku: null,
      analysisRequestId: '',
      processing: false,
      performance: {
        activeImageTasks: 0,
        maxConcurrentImageTasks: 0,
        imageTimings: [],
        pipelineDurationMs: 0
      }
    };

    draftFiles.slice(1).forEach(revokeFilePreview);
    clearIntakeForm();
    renderDraftsQueue();
    processDraft(draftId);
  }

  function clearIntakeForm() {
    selectedFiles = [];

    if (dom.itemInfoInput) dom.itemInfoInput.value = '';
    if (dom.productLocationInput) dom.productLocationInput.value = '';
    if (dom.caseLocationInput) dom.caseLocationInput.value = '';
    if (dom.cameraInput) dom.cameraInput.value = '';
    if (dom.galleryInput) dom.galleryInput.value = '';

    renderPreviews();
    checkIntakeReady();
  }

  function renderDraftsQueue() {
    if (!dom.draftsList) return;

    dom.draftsList.replaceChildren();

    const draftKeys = Object.keys(drafts).reverse();

    if (draftKeys.length === 0) {
      const emptyMessage = document.createElement('div');
      emptyMessage.className = 'queue-empty';
      emptyMessage.textContent = 'Queue is empty.';
      dom.draftsList.appendChild(emptyMessage);
      return;
    }

    for (const id of draftKeys) {
      const draft = drafts[id];
      dom.draftsList.appendChild(buildDraftCard(draft));
    }
  }

  function buildDraftCard(draft) {
    const card = document.createElement('div');
    const safeStatus = VALID_DRAFT_STATUSES.has(draft.status) ? draft.status : 'uploading';
    card.id = `draft-card-${draft.id}`;
    card.className = `draft-card status-${safeStatus}`;

    const thumbnail = document.createElement('img');
    thumbnail.src = draft.thumbUrl || EMPTY_GIF;
    thumbnail.className = 'draft-thumb';
    thumbnail.alt = draft.thumbUrl ? 'Draft product thumbnail' : '';

    const info = document.createElement('div');
    info.className = 'draft-info';

    const title = document.createElement('div');
    title.className = 'draft-title';
    title.textContent = draft.info ? truncateText(draft.info, 30) : 'New Product';

    const status = document.createElement('div');
    status.className = 'draft-status';
    fillDraftStatus(status, draft);

    info.append(title, status);

    const actionWrap = document.createElement('div');
    actionWrap.className = 'draft-action-wrap';

    if (draft.status === 'ready') {
      const reviewButton = document.createElement('button');
      reviewButton.type = 'button';
      reviewButton.className = 'draft-action btn-review';
      reviewButton.textContent = 'Review';
      reviewButton.addEventListener('click', event => {
        event.stopPropagation();
        openReviewModal(draft.id);
      });

      actionWrap.appendChild(reviewButton);

      card.addEventListener('click', event => {
        if (event.target instanceof Element && event.target.closest('button, a')) return;
        openReviewModal(draft.id);
      });
    } else if (draft.status === 'error') {
      const retryButton = document.createElement('button');
      const hasPendingUploads = draft.files.some(
        (file, index) => file && !draft.fileIds[index]
      );
      retryButton.type = 'button';
      retryButton.className = 'draft-action btn-retry';
      retryButton.textContent = hasPendingUploads
        ? 'Retry upload'
        : 'Retry analysis';
      retryButton.disabled = Boolean(draft.processing);
      retryButton.addEventListener('click', event => {
        event.stopPropagation();
        processDraft(draft.id);
      });
      actionWrap.appendChild(retryButton);
    } else if (draft.status === 'submitted') {
      const done = document.createElement('span');
      done.className = 'draft-done';
      done.textContent = '✅ Done';
      actionWrap.appendChild(done);
    }

    card.append(thumbnail, info, actionWrap);
    return card;
  }

  function updateDraftProgress(draft) {
    const card = document.getElementById(`draft-card-${draft.id}`);
    const status = card?.querySelector('.draft-status');
    if (!status) {
      renderDraftsQueue();
      return;
    }

    status.replaceChildren();
    fillDraftStatus(status, draft);
  }

  function fillDraftStatus(statusElement, draft) {
    if (draft.status !== 'submitted' || !draft.appSheetSku) {
      statusElement.textContent = draft.progressText || '';
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = `https://www.appsheet.com/start/${encodeURIComponent(MY_APPSHEET_ID)}#control=Products_Detail&row=${encodeURIComponent(String(draft.appSheetSku))}`;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.textContent = `View ${draft.appSheetSku} in AppSheet`;

    statusElement.appendChild(anchor);
  }

  async function processDraft(id) {
    const draft = drafts[id];
    if (!draft || draft.processing) return;
    draft.processing = true;

    try {
      const pipelineStartedAt = performance.now();
      let completed = Array.isArray(draft.fileIds)
        ? draft.fileIds.filter(Boolean).length
        : 0;
      const files = [...draft.files];
      const fileIds = Array.isArray(draft.fileIds)
        ? [...draft.fileIds]
        : [];
      const imageCount = Math.max(files.length, fileIds.length);
      draft.fileIds = fileIds;
      const uploadTasks = [];

      for (let index = 0; index < imageCount; index++) {
        if (fileIds[index]) continue;
        const file = files[index];
        if (!file) {
          throw new Error(
            `Photo ${index + 1} is no longer available. Please add the product again.`
          );
        }
        const queuedAt = performance.now();
        uploadTasks.push(
          runImagePipelineTask(async () => {
            const taskStartedAt = performance.now();
            draft.performance.activeImageTasks++;
            draft.performance.maxConcurrentImageTasks = Math.max(
              draft.performance.maxConcurrentImageTasks,
              draft.performance.activeImageTasks
            );

            try {
              const compressionStartedAt = performance.now();
              const base64Data = await compressImageAsync(file);
              const compressionDurationMs =
                performance.now() - compressionStartedAt;
              const uploadStartedAt = performance.now();
              const res = await apiFetch('uploadImage', {
                base64Data,
                fileName: `prod_${Date.now()}_${index}.jpg`,
                draftId: draft.id,
                requestPurpose: 'image_upload',
                productLocation: draft.productLoc,
                caseLocation: draft.caseLoc
              });
              const uploadDurationMs = performance.now() - uploadStartedAt;

              fileIds[index] = res.fileId;
              draft.fileIds[index] = res.fileId;
              completed++;
              draft.performance.imageTimings[index] = {
                index,
                queueWaitMs: Math.round(taskStartedAt - queuedAt),
                compressionMs: Math.round(compressionDurationMs),
                uploadMs: Math.round(uploadDurationMs),
                totalMs: Math.round(performance.now() - taskStartedAt)
              };
              files[index] = null;
              draft.files[index] = null;
              draft.progressText = `Uploading photo ${completed}/${imageCount}...`;
              updateDraftProgress(draft);
            } finally {
              draft.performance.activeImageTasks--;
            }
          })
        );
      }

      if (uploadTasks.length > 0) {
        draft.status = 'uploading';
        draft.progressText = `Uploading photo ${completed}/${imageCount}...`;
        renderDraftsQueue();
        const uploadResults = await Promise.allSettled(uploadTasks);
        const failedUpload = uploadResults.find(result => result.status === 'rejected');
        draft.performance.pipelineDurationMs += Math.round(
          performance.now() - pipelineStartedAt
        );
        if (failedUpload) throw failedUpload.reason;
      }

      draft.fileIds = fileIds.filter(Boolean);
      draft.files = [];
      draft.status = 'analyzing';
      draft.progressText = 'AI is analyzing... 🧠';
      renderDraftsQueue();

      const analysisRequestId = createClientRequestId();
      draft.analysisRequestId = analysisRequestId;
      draft.aiData = await apiFetch('analyzeImages', {
        requestId: analysisRequestId,
        draftId: draft.id,
        requestPurpose: 'initial_analysis',
        fileIds: draft.fileIds,
        itemInfo: draft.info,
        productLocation: draft.productLoc,
        caseLocation: draft.caseLoc,
        clientMetrics: summarizeDraftPerformance(draft)
      });

      draft.status = 'ready';
      draft.progressText = 'Ready for your review';
      renderDraftsQueue();
    } catch (err) {
      draft.status = 'error';
      draft.progressText = `Error: ${err.message || err}`;
    } finally {
      draft.processing = false;
      if (draft.status === 'error') renderDraftsQueue();
    }
  }

  function createTaskLimiter(maxConcurrent) {
    const queue = [];
    let activeCount = 0;

    function drain() {
      while (activeCount < maxConcurrent && queue.length > 0) {
        const next = queue.shift();
        activeCount++;
        Promise.resolve()
          .then(next.task)
          .then(next.resolve, next.reject)
          .finally(() => {
            activeCount--;
            drain();
          });
      }
    }

    return task => new Promise((resolve, reject) => {
      queue.push({ task, resolve, reject });
      drain();
    });
  }

  function summarizeDraftPerformance(draft) {
    const timings = draft.performance?.imageTimings.filter(Boolean) || [];
    const totals = timings.reduce(
      (result, timing) => {
        result.totalCompressionMs += timing.compressionMs;
        result.totalUploadMs += timing.uploadMs;
        result.totalQueueWaitMs += timing.queueWaitMs;
        result.maxImageTaskMs = Math.max(
          result.maxImageTaskMs,
          timing.totalMs
        );
        result.maxQueueWaitMs = Math.max(
          result.maxQueueWaitMs,
          timing.queueWaitMs
        );
        return result;
      },
      {
        totalCompressionMs: 0,
        totalUploadMs: 0,
        totalQueueWaitMs: 0,
        maxImageTaskMs: 0,
        maxQueueWaitMs: 0
      }
    );
    const summary = {
      imageCount: timings.length,
      pipelineDurationMs: draft.performance?.pipelineDurationMs || 0,
      maxConcurrentImageTasks:
        draft.performance?.maxConcurrentImageTasks || 0,
      ...totals
    };
    if (Number.isFinite(draft.performance?.reviewOpenMs)) {
      summary.reviewOpenMs = draft.performance.reviewOpenMs;
    }
    return summary;
  }



  function stageFileRead(file) {
    if (!file) return Promise.resolve({ error: new Error('No image file was provided.') });
    const existingRead = stagedFileReads.get(file);
    if (existingRead) return existingRead;

    const stagedRead = readFileAsDataUrl(file).then(
      dataUrl => ({ dataUrl }),
      error => ({ error })
    );
    stagedFileReads.set(file, stagedRead);
    return stagedRead;
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = event => resolve(event.target?.result || '');
      reader.onerror = () => reject(
        new Error(`Failed to read ${file?.name || 'photo'} from device storage. Please reselect it.`)
      );
      reader.onabort = () => reject(
        new Error(`Reading ${file?.name || 'photo'} was interrupted. Please reselect it.`)
      );
      reader.readAsDataURL(file);
    });
  }

  async function compressImageAsync(file) {
    const stagedRead = await stageFileRead(file);
    if (stagedRead.error) {
      stagedFileReads.delete(file);
      throw stagedRead.error;
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          let { width, height } = img;
          const maxSize = 2048;
          if (width > height) {
            if (width > maxSize) {
              height = Math.floor(height * (maxSize / width));
              width = maxSize;
            }
          } else if (height > maxSize) {
            width = Math.floor(width * (maxSize / height));
            height = maxSize;
          }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.92));
        } catch (err) {
          reject(new Error('Canvas memory crash. Image too large for this device.'));
        }
      };
      img.onerror = () => reject(new Error('Browser could not read this image format (Likely HEIC).'));
      img.src = stagedRead.dataUrl;
    });
  }

  // ── Review Modal Logic ─────────────────────────────────

  function openReviewModal(id) {
    const draft = drafts[id];

    if (!draft || draft.status !== 'ready') {
      return;
    }

    const reviewStartedAt = performance.now();
    currentReviewDraftId = id;

    const ai = draft.aiData || {};
    const listing = ai.listing && typeof ai.listing === 'object'
      ? ai.listing
      : ai;
    const identification = ai.identification && typeof ai.identification === 'object'
      ? ai.identification
      : null;
    const catalogResearch = ai.catalog_research && typeof ai.catalog_research === 'object'
      ? ai.catalog_research
      : {};

    setInputValue('edit-title', listing.title || ai.title || '');
    setInputValue('edit-brand', identification?.brand || ai.brand || '');
    setInputValue('edit-category', listing.category || ai.category || '');
    setProductTypeReviewValue(listing.type || ai.type || '');
    if (!productTypeReferenceReady) loadReferenceData();
    renderShippingAttributeFields();
    setInputValue('edit-condition', listing.condition || ai.condition || 'Used');
    setInputValue('edit-price', listing.price || ai.price || '');
    setInputValue(
      'edit-compare',
      listing.comparePrice ||
      listing.compare_price ||
      ai.comparePrice ||
      ai.compare_price ||
      ''
    );
    setInputValue('edit-cost', listing.cost || ai.cost || '');
    setInputValue(
      'edit-seo-title',
      listing.seo_title || listing.seoTitle || ai.seo_title || ai.seoTitle || ''
    );
    setInputValue(
      'edit-seo-description',
      listing.seo_description ||
      listing.seoDescription ||
      ai.seo_description ||
      ai.seoDescription ||
      ''
    );
    setInputValue('edit-tags', formatTags(listing.tags || ai.tags));
    setInputValue('edit-gtin', catalogResearch.gtin || '');
    setInputValue(
      'edit-product-weight',
      catalogResearch.product_weight_value == null
        ? ''
        : catalogResearch.product_weight_value
    );
    setInputValue(
      'edit-product-weight-unit',
      catalogResearch.product_weight_unit || ''
    );
    setInputValue(
      'edit-description',
      formatDescription(getListingDescription(listing, ai))
    );
    setInputValue('edit-notes', '');
    renderIdentificationReview(identification);
    renderCatalogResearch(catalogResearch);
    renderAnalysisModelReview(ai);

    if (dom.reviewModal) {
      document.body.classList.add('review-open');
      dom.reviewModal.hidden = false;
      document.getElementById('edit-title')?.focus();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          draft.performance.reviewOpenMs = Math.round(
            performance.now() - reviewStartedAt
          );
          console.info('AI intake review rendered', {
            draftId: draft.id,
            reviewOpenMs: draft.performance.reviewOpenMs
          });
        });
      });
    }
  }

  function closeReviewModal() {
    if (dom.reviewModal) {
      dom.reviewModal.hidden = true;
    }

    document.body.classList.remove('review-open');
    currentReviewDraftId = null;
  }

  function renderAnalysisModelReview(ai) {
    const metadata = ai?.analysis_metadata || {};
    const usedModel = String(metadata.model || '').trim();
    const modelAttemptNumber = Number(metadata.modelAttemptNumber);
    const availableModels = Array.isArray(metadata.availableModels)
      ? metadata.availableModels.filter(Boolean)
      : (usedModel ? [usedModel] : []);

    if (dom.analysisModelName) {
      dom.analysisModelName.textContent = usedModel
        ? `${usedModel}${modelAttemptNumber > 1 ? ` (attempt ${modelAttemptNumber})` : ''}`
        : 'Unknown';
    }

    if (dom.retryModelSelect) {
      const fragment = document.createDocumentFragment();
      for (const modelName of availableModels) {
        const option = document.createElement('option');
        option.value = String(modelName);
        option.textContent = String(modelName);
        fragment.appendChild(option);
      }
      dom.retryModelSelect.replaceChildren(fragment);
      if (usedModel && availableModels.includes(usedModel)) {
        dom.retryModelSelect.value = usedModel;
      }
      dom.retryModelSelect.disabled = availableModels.length === 0;
    }

    if (dom.retryModelBtn) {
      dom.retryModelBtn.disabled = availableModels.length === 0;
    }
    if (dom.retryModelStatus) {
      dom.retryModelStatus.textContent = '';
    }
  }

  async function retryAnalysisWithSelectedModel() {
    const draft = drafts[currentReviewDraftId];
    const selectedModel = String(dom.retryModelSelect?.value || '').trim();
    if (!draft || !selectedModel || draft.processing) return;

    const preservedNotes = getInputValue('edit-notes');
    const preservedStaffName = getInputValue('staff-name');
    draft.processing = true;
    if (dom.retryModelBtn) {
      dom.retryModelBtn.disabled = true;
      dom.retryModelBtn.textContent = 'Retrying Analysis...';
    }
    if (dom.retryModelSelect) dom.retryModelSelect.disabled = true;
    if (dom.submitShopifyBtn) dom.submitShopifyBtn.disabled = true;
    if (dom.retryModelStatus) {
      dom.retryModelStatus.textContent = `Running ${selectedModel} against the existing images...`;
    }

    try {
      const retryOfRequestId = draft.analysisRequestId;
      const analysisRequestId = createClientRequestId();
      draft.analysisRequestId = analysisRequestId;
      const refreshedAiData = await apiFetch('analyzeImages', {
        requestId: analysisRequestId,
        draftId: draft.id,
        requestPurpose: 'manual_model_retry',
        retryOfRequestId,
        fileIds: draft.fileIds,
        itemInfo: draft.info,
        model: selectedModel,
        knownProductTitle: getInputValue('edit-title'),
        productLocation: draft.productLoc,
        caseLocation: draft.caseLoc,
        clientMetrics: summarizeDraftPerformance(draft)
      });
      draft.aiData = refreshedAiData;
      if (currentReviewDraftId === draft.id) {
        openReviewModal(draft.id);
        setInputValue('edit-notes', preservedNotes);
        setInputValue('staff-name', preservedStaffName);
        if (dom.retryModelStatus) {
          dom.retryModelStatus.textContent =
            `Analysis replaced using ${draft.aiData?.analysis_metadata?.model || selectedModel}.`;
        }
      }
    } catch (err) {
      if (dom.retryModelStatus) {
        dom.retryModelStatus.textContent =
          `Retry failed: ${err.message || err}. Current review fields were kept.`;
      }
    } finally {
      draft.processing = false;
      if (dom.retryModelBtn) {
        dom.retryModelBtn.disabled = false;
        dom.retryModelBtn.textContent = 'Retry & Replace AI Fields';
      }
      if (dom.retryModelSelect) dom.retryModelSelect.disabled = false;
      if (dom.submitShopifyBtn) dom.submitShopifyBtn.disabled = false;
    }
  }

  function renderIdentificationReview(identification) {
    if (!dom.identificationReview) return;
    if (!identification) {
      dom.identificationReview.hidden = true;
      return;
    }

    const confidence = String(identification.confidence || 'LOW').toUpperCase();
    const needsReview = Boolean(identification.needs_human_verification);
    const summaryParts = [
      identification.brand,
      identification.model,
      identification.variant,
      identification.exact_year || identification.approximate_era,
      identification.country_of_origin
    ].filter(Boolean);
    if (identification.serial_number) {
      summaryParts.push(`Serial: ${identification.serial_number}`);
    }

    if (dom.identificationStatus) {
      dom.identificationStatus.textContent = needsReview
        ? `${confidence} CONFIDENCE · VERIFY`
        : `${confidence} CONFIDENCE`;
      dom.identificationStatus.dataset.confidence = confidence.toLowerCase();
      dom.identificationStatus.dataset.review = needsReview ? 'required' : 'clear';
    }
    if (dom.identificationSummary) {
      dom.identificationSummary.textContent = summaryParts.length > 0
        ? summaryParts.join(' · ')
        : 'No specific maker/model identification was asserted.';
    }
    renderTextList(
      dom.identificationEvidence,
      Array.isArray(identification.evidence)
        ? identification.evidence.map(item => {
            const source = item.image_index == null
              ? 'Staff note'
              : `Image ${item.image_index}`;
            return `${item.field}: ${item.observation} (${source})`;
          })
        : [],
      'No identification evidence was returned.'
    );
    renderTextList(
      dom.identificationUncertainties,
      Array.isArray(identification.uncertainties)
        ? identification.uncertainties
        : [],
      'No material uncertainties were reported.'
    );
    dom.identificationReview.hidden = false;
  }

  function renderCatalogResearch(research) {
    if (!dom.catalogResearchReview) return;
    const warnings = Array.isArray(research?.warnings)
      ? research.warnings.filter(Boolean)
      : [];
    const notes = [research?.notes, ...warnings].filter(Boolean);
    if (dom.catalogResearchNotes) {
      dom.catalogResearchNotes.textContent = notes.length > 0
        ? notes.join(' ')
        : 'No verified GTIN or product weight was found.';
    }
    if (dom.catalogResearchSources) {
      const fragment = document.createDocumentFragment();
      const sources = Array.isArray(research?.sources) ? research.sources : [];
      for (const source of sources) {
        if (!source?.url) continue;
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = source.url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.textContent = source.title || 'Research source';
        item.appendChild(link);
        fragment.appendChild(item);
      }
      dom.catalogResearchSources.replaceChildren(fragment);
    }
    dom.catalogResearchReview.hidden = false;
  }

  function getListingDescription(listing, ai) {
    if (Array.isArray(listing.description)) {
      return listing.description;
    }
    return ai.description;
  }

  function renderTextList(listElement, values, emptyMessage) {
    if (!listElement) return;
    const fragment = document.createDocumentFragment();
    const items = values.length > 0 ? values : [emptyMessage];
    for (const value of items) {
      const item = document.createElement('li');
      item.textContent = value;
      if (values.length === 0) item.className = 'identification-empty';
      fragment.appendChild(item);
    }
    listElement.replaceChildren(fragment);
  }

  async function submitDraftToShopify() {
    const draft = drafts[currentReviewDraftId];
    const btn = dom.submitShopifyBtn;

    if (!draft) {
      alert('No draft is currently open for review.');
      return;
    }

    const enteredPrice = document.getElementById('edit-price')?.value || '';
    const finalPrice = enteredPrice && Number(enteredPrice) > 0 ? enteredPrice : '99999';
    const shippingReview = collectShippingReviewData();
    const ai = draft.aiData || {};
    const listing = ai.listing && typeof ai.listing === 'object'
      ? ai.listing
      : ai;

    const finalData = {
      title: getInputValue('edit-title'),
      brand: getInputValue('edit-brand'),
      category: getInputValue('edit-category'),
      type: getInputValue('edit-type'),
      shippingAttributes: shippingReview.attributes,
      shippingClassOverride: shippingReview.override,
      condition: getInputValue('edit-condition'),
      price: finalPrice,
      comparePrice: getInputValue('edit-compare'),
      cost: getInputValue('edit-cost'),
      description: getInputValue('edit-description'),
      tags: getInputValue('edit-tags'),
      seoTitle: getInputValue('edit-seo-title'),
      seoDescription: getInputValue('edit-seo-description'),
      gtin: getInputValue('edit-gtin'),
      productWeight: getInputValue('edit-product-weight'),
      productWeightUnit: getInputValue('edit-product-weight-unit'),
      productLocation: draft.productLoc,
      caseLocation: draft.caseLoc,
      notes: getInputValue('edit-notes'),
      staffName: getInputValue('staff-name'),
      fileIds: draft.fileIds,
      imageAltTexts: Array.isArray(listing.image_alt_texts)
        ? listing.image_alt_texts
        : []
    };

    if (!finalData.title.trim()) {
      alert('Missing Title! A title is required to create a product.');
      return;
    }
    if (!finalData.type.trim()) {
      alert(productTypeReferenceReady
        ? 'Product Type is required. Gemini leaves it blank when no approved type is an explicit fit.'
        : 'Product Type options could not be loaded. Reload the page before submitting so the canonical Product Type and shipping rules are available.');
      return;
    }
    if (!productTypeReferenceReady) {
      alert('Product Type reference data is unavailable. Reload the page before submitting so shipping validation uses the canonical rules.');
      return;
    }
    if (shippingReview.missingLabels.length > 0) {
      alert(`Complete the required shipping field(s): ${shippingReview.missingLabels.join(', ')}.`);
      return;
    }
    if (shippingReview.overrideRequired && !shippingReview.override) {
      alert('GF Shipping Class Override is required for this item.');
      return;
    }

    if (btn) {
      btn.disabled = true;
      btn.textContent = 'Pushing to Shopify...';
    }

    try {
      const result = await apiFetch('submitFinal', {
        draftId: draft.id,
        requestPurpose: 'final_submission',
        finalData,
        clientMetrics: summarizeDraftPerformance(draft)
      });

      closeReviewModal();

      draft.status = 'submitted';
      draft.appSheetSku = result?.sku || '';
      const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
      draft.progressText = warnings.length > 0
        ? `Submitted to Shopify with ${warnings.length} follow-up warning(s).`
        : 'Submitted to Shopify.';
      renderDraftsQueue();

      if (warnings.length > 0) {
        alert(
          `The draft product was created, but these follow-up steps need attention:\n\n` +
          warnings.map(warning => `• ${warning.message}`).join('\n')
        );
      }

      window.setTimeout(() => {
        releaseDraftResources(draft);
        delete drafts[draft.id];
        renderDraftsQueue();
      }, 15000);
    } catch (err) {
      alert(`Error: ${err.message || err}`);
    } finally {
      if (btn) {
        btn.disabled = false;
        btn.textContent = '🚀 Submit to Shopify';
      }
    }
  }

  function setInputValue(id, value) {
    const input = document.getElementById(id);
    if (input) input.value = value == null ? '' : String(value);
  }

  function getInputValue(id) {
    return document.getElementById(id)?.value || '';
  }

  function formatTags(tags) {
    if (Array.isArray(tags)) return tags.join(', ');
    if (typeof tags === 'string') return tags;
    return '';
  }

  function formatDescription(description) {
    if (Array.isArray(description)) {
      const intro = description.slice(0, 3).filter(Boolean).join(' ');
      const rest = description.slice(3).filter(Boolean).join('\n');
      return [intro, rest].filter(Boolean).join('\n\n');
    }

    if (typeof description === 'string') {
      return description;
    }

    return '';
  }

  function truncateText(value, maxLength) {
    const text = String(value || '');
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  }

  function releaseDraftResources(draft) {
    if (!draft) return;
    revokePreviewUrl(draft.thumbUrl);
    draft.thumbUrl = null;
    draft.files = [];
  }
})();
